// `react-grid-layout/core` is the pure math subpath. It pulls no React, so the
// headless renderer can import this module under plain Node.
import { calcGridItemPosition } from "react-grid-layout/core";
import type { Layout } from "react-grid-layout";
import type { LayoutItem } from "react-grid-layout/legacy";
import {
  DEFAULT_GRID_COLS,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_GRID_MARGIN,
} from "../gridConstants";

// A4 landscape in points (72 pt = 1 in; 297 × 210 mm)
export const A4_WIDTH_PT = 841.89;
export const A4_HEIGHT_PT = 595.28;
export const A4_RATIO = A4_WIDTH_PT / A4_HEIGHT_PT; // ≈ 1.4142

/**
 * Max grid container width in CSS pixels.
 * This single value controls the A4 page region size on the grid.
 * Increase → wider (and proportionally taller) page region.
 * The CSS max-width on .dashboard-grid-container must match this value.
 */
export const GRID_MAX_WIDTH_PX = 1414;

/**
 * Half the vertical grid margin — used to center page-break guides
 * in the gutter between rows. Applied identically to the overlay
 * guide and the PDF page-break logic so they always agree.
 */
export const GUTTER_OFFSET_PX = DEFAULT_GRID_MARGIN[1] / 2;

/** Height of one "page" in CSS pixels at a given container width. */
export function pageHeightPx(containerWidth: number): number {
  return containerWidth / A4_RATIO;
}

/** Uniform scale factor from CSS pixels to PDF points. */
export function pdfScale(containerWidth: number): number {
  return A4_WIDTH_PT / containerWidth;
}

export interface PdfRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Compute PDF-point rects that exactly match the browser grid layout.
 *
 * Uses the same `calcGridItemPosition` the browser uses internally,
 * then uniformly scales by `A4_WIDTH_PT / containerWidthPx`.
 */
export function computePdfRects(
  layout: Layout,
  containerWidthPx: number,
): { scale: number; rects: Map<string, PdfRect> } {
  const positionParams = {
    margin: DEFAULT_GRID_MARGIN as readonly [number, number],
    containerPadding: DEFAULT_GRID_MARGIN as readonly [number, number],
    containerWidth: containerWidthPx,
    cols: DEFAULT_GRID_COLS,
    rowHeight: DEFAULT_ROW_HEIGHT,
    maxRows: 9999,
  };

  const scale = pdfScale(containerWidthPx);
  const rects = new Map<string, PdfRect>();

  for (const item of layout) {
    const r = calcGridItemPosition(positionParams, item.x, item.y, item.w, item.h);
    rects.set(item.i, {
      left: r.left * scale,
      top: r.top * scale,
      width: r.width * scale,
      height: r.height * scale,
    });
  }

  return { scale, rects };
}

export interface PdfPagination {
  scale: number;
  rects: Map<string, PdfRect>;
  /** Page breaks sit half a gutter below the row edge, matching the overlay guide. */
  gutterOffsetPt: number;
  /** 0-based page index → the items visible on that page. A straddling item is on both. */
  pageItems: Map<number, LayoutItem[]>;
  numPages: number;
}

/**
 * Split the layout into A4 pages. The document renders from this, and the page
 * count a compiled report needs comes from the same split, so the two never differ.
 */
export function paginateLayout(layout: Layout, containerWidthPx: number): PdfPagination {
  const { scale, rects } = computePdfRects(layout, containerWidthPx);
  const gutterOffsetPt = GUTTER_OFFSET_PX * scale;
  const pageItems = new Map<number, LayoutItem[]>();
  let maxPageIndex = 0;

  for (const item of layout) {
    const rect = rects.get(item.i);
    if (!rect) continue;
    const firstPage = Math.max(0, Math.floor((rect.top - gutterOffsetPt) / A4_HEIGHT_PT));
    const lastPage = Math.max(0, Math.floor((rect.top + rect.height - 1 - gutterOffsetPt) / A4_HEIGHT_PT));
    maxPageIndex = Math.max(maxPageIndex, lastPage);
    for (let p = firstPage; p <= lastPage; p++) {
      const arr = pageItems.get(p) ?? [];
      arr.push(item as LayoutItem);
      pageItems.set(p, arr);
    }
  }

  return { scale, rects, gutterOffsetPt, pageItems, numPages: Math.max(1, maxPageIndex + 1) };
}

/** How many PDF pages the layout spans. */
export function countPdfPages(layout: Layout, containerWidthPx: number): number {
  return paginateLayout(layout, containerWidthPx).numPages;
}

/**
 * Compute the total grid height in CSS pixels from the layout.
 * Used by the page-break overlay to know how far down to draw guides.
 */
export function gridHeightPx(layout: Layout, containerWidthPx: number): number {
  const positionParams = {
    margin: DEFAULT_GRID_MARGIN as readonly [number, number],
    containerPadding: DEFAULT_GRID_MARGIN as readonly [number, number],
    containerWidth: containerWidthPx,
    cols: DEFAULT_GRID_COLS,
    rowHeight: DEFAULT_ROW_HEIGHT,
    maxRows: 9999,
  };

  let maxBottom = 0;
  for (const item of layout) {
    const r = calcGridItemPosition(positionParams, item.x, item.y, item.w, item.h);
    maxBottom = Math.max(maxBottom, r.top + r.height);
  }
  return maxBottom;
}
